import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  ArrowLeftIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  PersonIcon,
  ListBulletIcon,
  ResetIcon,
  Pencil2Icon,
  ZoomInIcon,
  ZoomOutIcon,
  DotFilledIcon,
} from '@radix-ui/react-icons';
import type { BoardPoint, BoardSnapshot, ProblemMoveIntent, StoneColor } from '@wx-weiqi/contracts';

import {
  canReviewHomeworkSubmission,
  sortHomeworkGradingRows,
  summarizeHomeworkProblem,
  type HomeworkProblemGradingSummary,
} from '../domain/homework-grading';
import { homeworkBoardFocus } from '../domain/homework-board-focus';
import {
  formatHomeworkElapsedTime,
  formatHomeworkSubmittedAt,
} from '../domain/homework-review-presentation';
import {
  branchResearchVariation,
  researchStoneLabels,
  researchVariationPrefix,
} from '../domain/fox-kifu-presentation';
import type { HomeworkStore } from '../stores/homework-store';
import type { TeachingStore } from '../stores/teaching-store';
import { BoardSurface } from './BoardSurface';
import { HomeworkMoveControls } from './HomeworkMoveControls';
import { HomeworkMarkerControl } from './HomeworkMarkerControl';
import { useBoardWheelNavigation } from './useBoardWheelNavigation';
import type { ClassroomMarkerMode } from '../domain/classroom-marker-display';
import { homeworkVisibleStoneLabels } from '../domain/homework-replay-presentation';
import {
  ReviewAnnotationLayer,
  toggleReviewPointAnnotation,
  type ReviewAnnotation,
  type ReviewAnnotationColor,
  type ReviewAnnotationTool,
  type ReviewBrushStroke,
} from './ReviewAnnotationLayer';
import { ClassroomAnnotationToolbar } from './ClassroomAnnotationToolbar';
import {
  HomeworkSubmissionReplayViewer,
  type HomeworkSubmissionReplayMode,
} from './HomeworkSubmissionReplayViewer';

const EMPTY_BOARD: BoardSnapshot = {
  ruleset: 'CHINESE_AREA_V1',
  size: 19,
  currentPlayer: 'BLACK',
  stones: Array.from({ length: 361 }, () => null),
  positionHash: '0'.repeat(64),
};

export type HomeworkGradingFullscreenAction =
  'EXIT_NATIVE' | 'EXIT_IMMERSIVE' | 'ENTER_NATIVE' | 'ENTER_IMMERSIVE';

export function homeworkGradingFullscreenAction({
  hasNativeFullscreenElement,
  immersive,
  canRequestNativeFullscreen,
}: {
  readonly hasNativeFullscreenElement: boolean;
  readonly immersive: boolean;
  readonly canRequestNativeFullscreen: boolean;
}): HomeworkGradingFullscreenAction {
  if (hasNativeFullscreenElement) return 'EXIT_NATIVE';
  if (immersive) return 'EXIT_IMMERSIVE';
  return canRequestNativeFullscreen ? 'ENTER_NATIVE' : 'ENTER_IMMERSIVE';
}

function statusLabel(status: 'CORRECT' | 'INCORRECT' | 'PENDING' | 'UNANSWERED'): string {
  if (status === 'CORRECT') return '正确';
  if (status === 'INCORRECT') return '错误';
  if (status === 'PENDING') return '待批改';
  return '未提交';
}

function averageTimeLabel(elapsedMs: number | null): string {
  return elapsedMs === null
    ? '暂无记录'
    : formatHomeworkElapsedTime(Math.round(elapsedMs)).replace(/^用时\s*/u, '');
}

export function HomeworkGradingSummaryBar({
  summary,
}: {
  readonly summary: HomeworkProblemGradingSummary;
}) {
  const rate =
    summary.rows.length === 0 ? 0 : Math.round((summary.correct / summary.rows.length) * 100);
  return (
    <section className="grading-summary-bar" aria-label={`本题统计，正确率 ${rate}%`}>
      <div className="grading-summary-counts">
        {(
          [
            ['correct', '正确', summary.correct],
            ['incorrect', '错误', summary.incorrect],
            ['pending', '待批改', summary.pending],
            ['unanswered', '未提交', summary.unanswered],
          ] as const
        ).map(([status, label, count]) => (
          <span key={status}>
            <DotFilledIcon className={`grading-status--${status}`} aria-hidden="true" />
            {label} {count}
          </span>
        ))}
      </div>
      <span className="grading-average" title={`${summary.timedSampleCount} 人有计时记录`}>
        平均用时 <strong>{averageTimeLabel(summary.averageElapsedMs)}</strong>
      </span>
    </section>
  );
}

export function HomeworkGradingErrorNotice({
  message,
  canRetry,
  onRetry,
  onDismiss,
}: {
  readonly message: string;
  readonly canRetry: boolean;
  readonly onRetry: () => void;
  readonly onDismiss: () => void;
}) {
  return (
    <div className="homework-grading-error" role="alert">
      <span>{message}</span>
      <button type="button" onClick={canRetry ? onRetry : onDismiss}>
        {canRetry ? '重新加载' : '关闭提示'}
      </button>
    </div>
  );
}

export function HomeworkGradingQuestionNavigation({
  questionIndex,
  questionCount,
  onPrevious,
  onNext,
}: {
  readonly questionIndex: number;
  readonly questionCount: number;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
}) {
  return (
    <nav className="homework-grading-question-nav" aria-label="作业题目导航">
      <button type="button" disabled={questionIndex <= 0} onClick={onPrevious}>
        上一题
      </button>
      <strong>
        {Math.min(questionIndex + 1, questionCount || 1)} / {Math.max(1, questionCount)}
      </strong>
      <button type="button" disabled={questionIndex >= questionCount - 1} onClick={onNext}>
        下一题
      </button>
    </nav>
  );
}

export function HomeworkGradingPage({
  homeworkId,
  homework,
  teaching,
  onBack,
}: {
  readonly homeworkId: string;
  readonly homework: HomeworkStore;
  readonly teaching: TeachingStore;
  readonly onBack: () => void;
}) {
  const homeworkState = useSyncExternalStore(homework.subscribe, homework.snapshot);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const pageRef = useRef<HTMLElement | null>(null);
  const boardStageRef = useRef<HTMLDivElement | null>(null);
  const researchRequest = useRef(0);
  const assignmentsLoadRequest = useRef(0);
  const [replayControlsContainer, setReplayControlsContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [questionIndex, setQuestionIndex] = useState(0);
  const studentListRef = useRef<HTMLDivElement | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [assignmentsLoadFailed, setAssignmentsLoadFailed] = useState(false);
  const [replayTarget, setReplayTarget] = useState<{
    readonly submissionId: string;
    readonly mode: HomeworkSubmissionReplayMode;
  } | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [researching, setResearching] = useState(false);
  const [researchPending, setResearchPending] = useState(false);
  const [researchMoves, setResearchMoves] = useState<readonly BoardPoint[]>([]);
  const [researchBoards, setResearchBoards] = useState<readonly BoardSnapshot[]>([]);
  const [researchCursor, setResearchCursor] = useState(0);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationTool, setAnnotationTool] = useState<ReviewAnnotationTool>('BRUSH');
  const [annotationColor, setAnnotationColor] = useState<ReviewAnnotationColor>('RED');
  const [annotationWidth, setAnnotationWidth] = useState(7);
  const [annotations, setAnnotations] = useState<readonly ReviewAnnotation[]>([]);
  const [markerMode, setMarkerMode] = useState<ClassroomMarkerMode>('ALL');
  const [customMarkerCount, setCustomMarkerCount] = useState(10);
  const [markerOptionsOpen, setMarkerOptionsOpen] = useState(false);

  const assignments =
    homeworkState.assignments.length > 0 && homeworkState.assignments[0]?.homeworkId === homeworkId
      ? homeworkState.assignments
      : (homeworkState.reviewAssignmentsByHomework[homeworkId] ?? []);
  const items = assignments[0]?.items ?? [];
  const currentItem = items[Math.min(questionIndex, Math.max(0, items.length - 1))];
  const problemId = currentItem?.problem.problemId ?? '';
  const baseBoard = currentItem?.problem.initialPosition ?? EMPTY_BOARD;
  const summary = useMemo(
    () => summarizeHomeworkProblem(assignments, problemId),
    [assignments, problemId],
  );
  const rows = useMemo(() => sortHomeworkGradingRows(summary.rows), [summary.rows]);
  const studentNames = useMemo(
    () =>
      new Map(
        teachingState.students.map(
          (student) => [student.membershipId, student.user.displayName] as const,
        ),
      ),
    [teachingState.students],
  );
  const selectedRow =
    rows.find((row) => row.assignment.studentMembershipId === selectedStudentId) ?? rows[0];
  const selectedSubmissionId = selectedRow?.submission?.submissionId ?? null;
  const focus = useMemo(() => homeworkBoardFocus(baseBoard), [baseBoard]);
  const viewport = zoomed ? (focus?.viewport ?? null) : null;
  const activeResearchMoves = useMemo(
    () => researchVariationPrefix(researchMoves, researchCursor),
    [researchCursor, researchMoves],
  );
  const displayedBoard = researching ? (researchBoards[researchCursor] ?? baseBoard) : baseBoard;
  const researchLabels = useMemo(
    () =>
      researching
        ? homeworkVisibleStoneLabels(
            researchStoneLabels(activeResearchMoves, displayedBoard.stones, displayedBoard.size),
            markerMode,
            customMarkerCount,
          )
        : [],
    [activeResearchMoves, customMarkerCount, displayedBoard, markerMode, researching],
  );
  const busy =
    homeworkState.pending === 'load-submissions' || homeworkState.pending === 'review-submission';

  const loadAssignments = useCallback(() => {
    const request = assignmentsLoadRequest.current + 1;
    assignmentsLoadRequest.current = request;
    setAssignmentsLoadFailed(false);
    void homework
      .loadHomeworkAssignments(homeworkId)
      .then(() => {
        if (assignmentsLoadRequest.current === request) setAssignmentsLoadFailed(false);
      })
      .catch(() => {
        if (assignmentsLoadRequest.current === request) setAssignmentsLoadFailed(true);
      });
  }, [homework, homeworkId]);

  useEffect(() => {
    loadAssignments();
    void teaching.loadTeacherDashboard().catch(() => undefined);
    return () => {
      assignmentsLoadRequest.current += 1;
    };
  }, [loadAssignments, teaching]);

  useEffect(() => {
    researchRequest.current += 1;
    homework.cancelHomeworkResearch();
    setQuestionIndex((current) => Math.min(current, Math.max(0, items.length - 1)));
    if (studentListRef.current !== null) studentListRef.current.scrollTop = 0;
    setSelectedStudentId('');
    setReplayTarget(null);
    setZoomed(false);
    setResearching(false);
    setResearchPending(false);
    setResearchMoves([]);
    setResearchBoards([]);
    setResearchCursor(0);
    setAnnotationOpen(false);
    setMarkerOptionsOpen(false);
    setAnnotations([]);
    return () => {
      researchRequest.current += 1;
      homework.cancelHomeworkResearch();
    };
  }, [homework, problemId]);

  useEffect(() => {
    if (
      selectedStudentId !== '' &&
      rows.some((row) => row.assignment.studentMembershipId === selectedStudentId)
    ) {
      return;
    }
    const firstSubmitted = rows.find((row) => row.submission !== null) ?? rows[0];
    setSelectedStudentId(firstSubmitted?.assignment.studentMembershipId ?? '');
  }, [rows, selectedStudentId]);

  useEffect(() => {
    setReplayTarget((current) => {
      if (current === null) return null;
      if (selectedSubmissionId === null) return null;
      if (current.submissionId === selectedSubmissionId) return current;
      return { ...current, submissionId: selectedSubmissionId };
    });
  }, [selectedSubmissionId]);

  useEffect(() => {
    const update = () => setImmersive(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const endResearch = () => {
    researchRequest.current += 1;
    homework.cancelHomeworkResearch();
    setResearching(false);
    setResearchPending(false);
    setResearchMoves([]);
    setResearchBoards([]);
    setResearchCursor(0);
  };

  const toggleResearch = () => {
    if (researching) {
      endResearch();
      return;
    }
    setReplayTarget(null);
    setResearching(true);
    setResearchMoves([]);
    setResearchBoards([baseBoard]);
    setResearchCursor(0);
  };

  const placeResearchMove = (point: BoardPoint) => {
    if (!researching || researchPending || problemId === '') return;
    const nextMoves = branchResearchVariation(researchMoves, researchCursor, point);
    const nextBoards = researchBoards.slice(0, researchCursor + 1);
    const request = researchRequest.current + 1;
    researchRequest.current = request;
    setResearchPending(true);
    void homework
      .researchHomeworkProblem(
        homeworkId,
        problemId,
        nextMoves.map((move): ProblemMoveIntent => ({ kind: 'PLAY', point: move })),
      )
      .then((result) => {
        if (researchRequest.current !== request) return;
        setResearchMoves(nextMoves);
        setResearchBoards([...nextBoards, result.board]);
        setResearchCursor(nextMoves.length);
      })
      .catch(() => undefined)
      .finally(() => {
        if (researchRequest.current === request) setResearchPending(false);
      });
  };

  const addPointAnnotation = (point: BoardPoint) => {
    if (annotationTool === 'BRUSH') return;
    setAnnotations((current) =>
      toggleReviewPointAnnotation(current, point, annotationTool, annotationColor),
    );
  };

  const toggleFullscreen = async () => {
    const pageElement = pageRef.current;
    const action = homeworkGradingFullscreenAction({
      hasNativeFullscreenElement: document.fullscreenElement !== null,
      immersive,
      canRequestNativeFullscreen: pageElement?.requestFullscreen !== undefined,
    });
    if (action === 'EXIT_NATIVE') {
      await document.exitFullscreen().catch(() => undefined);
      setImmersive(false);
      return;
    }
    if (action === 'EXIT_IMMERSIVE') {
      setImmersive(false);
      return;
    }
    if (action === 'ENTER_NATIVE') {
      const requestFullscreen = pageElement?.requestFullscreen;
      if (requestFullscreen === undefined) {
        setImmersive(true);
        return;
      }
      await requestFullscreen.call(pageElement).catch(() => setImmersive(true));
      return;
    }
    setImmersive(true);
  };

  const openSelectedReplay = (mode: HomeworkSubmissionReplayMode) => {
    if (selectedSubmissionId === null) return;
    if (researching) endResearch();
    setAnnotationOpen(false);
    setReplayTarget((current) =>
      current?.submissionId === selectedSubmissionId && current.mode === mode
        ? null
        : { submissionId: selectedSubmissionId, mode },
    );
  };

  const retryAssignments = () => {
    loadAssignments();
  };

  const navigateQuestion = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= items.length) return;
    setQuestionIndex(nextIndex);
  };

  const selectedName =
    selectedRow === undefined
      ? '请选择学生'
      : (studentNames.get(selectedRow.assignment.studentMembershipId) ?? '当前学生');

  useBoardWheelNavigation(boardStageRef, {
    enabled: replayTarget === null,
    blocked: !researching || researchPending || annotationOpen || markerOptionsOpen,
    canPrevious: researchCursor > 0,
    canNext: researchCursor < researchMoves.length,
    context: `${problemId}:research`,
    onStep: (direction) =>
      setResearchCursor((current) =>
        Math.max(0, Math.min(researchMoves.length, current + direction)),
      ),
  });

  return (
    <main
      ref={pageRef}
      className={`board-workspace homework-grading-page${immersive ? ' homework-grading-page--immersive' : ''}`}
    >
      <div className="homework-grading-workspace">
        <section className="homework-grading-board-panel" aria-label="当前作业题目棋盘">
          <div className="teacher-board-frame" ref={boardStageRef}>
            {replayTarget !== null && selectedSubmissionId !== null ? (
              <HomeworkSubmissionReplayViewer
                key={`${problemId}:${selectedSubmissionId}`}
                store={homework}
                submissionId={selectedSubmissionId}
                mode={replayTarget.mode}
                viewport={viewport}
                fallbackBoard={baseBoard}
                controlsContainer={replayControlsContainer}
                presentation="studio"
                markerMode={markerMode}
                customMarkerCount={customMarkerCount}
                navigationBlocked={markerOptionsOpen}
              />
            ) : (
              <div className="homework-grading-board-stage">
                <BoardSurface
                  stoneAppearance="shell"
                  coordinateEdges={{ top: true, left: true }}
                  size={displayedBoard.size}
                  stones={displayedBoard.stones}
                  initialAnnotations={currentItem?.problem.initialAnnotations ?? []}
                  lastMove={null}
                  stoneLabels={researchLabels}
                  viewport={viewport}
                  annotationLayer={
                    <ReviewAnnotationLayer
                      size={displayedBoard.size}
                      open={annotationOpen}
                      tool={annotationTool}
                      color={annotationColor}
                      width={annotationWidth}
                      annotations={annotations}
                      viewport={viewport}
                      onStroke={(stroke: ReviewBrushStroke) =>
                        setAnnotations((current) => [...current, stroke])
                      }
                      onPoint={addPointAnnotation}
                    />
                  }
                  {...(researching && !annotationOpen
                    ? {
                        isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
                          stone === null && !researchPending,
                        onPoint: placeResearchMove,
                      }
                    : {})}
                />
              </div>
            )}
          </div>
        </section>
        <aside className="homework-grading-sidebar">
          <header className="grading-heading">
            <div className="grading-heading-content" inert={annotationOpen}>
              <button
                type="button"
                className="grading-back"
                onClick={onBack}
                aria-label="返回作业列表"
              >
                <ArrowLeftIcon aria-hidden="true" />
                返回
              </button>
              <h1>批改作业</h1>
              <HomeworkGradingQuestionNavigation
                questionIndex={questionIndex}
                questionCount={items.length}
                onPrevious={() => navigateQuestion(questionIndex - 1)}
                onNext={() => navigateQuestion(questionIndex + 1)}
              />
              <button
                type="button"
                className="homework-grading-fullscreen fullscreen-action"
                aria-label={immersive ? '退出全屏' : '进入全屏'}
                title={immersive ? '退出全屏' : '进入全屏'}
                aria-pressed={immersive}
                onClick={() => void toggleFullscreen()}
              >
                {immersive ? (
                  <ExitFullScreenIcon aria-hidden="true" />
                ) : (
                  <EnterFullScreenIcon aria-hidden="true" />
                )}
              </button>
            </div>
            {annotationOpen ? (
              <ClassroomAnnotationToolbar
                tool={annotationTool}
                color={annotationColor}
                width={annotationWidth}
                annotationCount={annotations.length}
                onTool={setAnnotationTool}
                onColor={setAnnotationColor}
                onWidth={setAnnotationWidth}
                onUndo={() => setAnnotations((current) => current.slice(0, -1))}
                onClear={() => setAnnotations([])}
                onClose={() => setAnnotationOpen(false)}
              />
            ) : null}
          </header>
          <section className="grading-roster" aria-labelledby="grading-roster-title">
            <header className="grading-roster-heading">
              <h2 id="grading-roster-title">学生作答</h2>
              <span>
                {rows.length - summary.unanswered} / {rows.length} 已提交 · 已提交优先
              </span>
            </header>
            <div
              className="homework-grading-student-list"
              ref={studentListRef}
              tabIndex={0}
              aria-label="学生作答列表，可滚动"
              aria-busy={busy}
            >
              <table className="grading-table">
                <thead>
                  <tr>
                    <th scope="col">学生</th>
                    <th scope="col">提交时间</th>
                    <th scope="col">用时</th>
                    <th scope="col">批改</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const membershipId = row.assignment.studentMembershipId;
                    const name = studentNames.get(membershipId) ?? `学生 ${membershipId.slice(-4)}`;
                    const canReview = canReviewHomeworkSubmission(row.submission);
                    const submittedAt = formatHomeworkSubmittedAt(row.submission?.submittedAt);
                    const review = (status: 'CORRECT' | 'INCORRECT') => {
                      setSelectedStudentId(membershipId);
                      if (!canReview || busy || row.submission === null) return;
                      void homework
                        .reviewSubmission(
                          row.submission.submissionId,
                          row.submission.submissionVersion,
                          status,
                          status === 'CORRECT' ? 100 : 0,
                          row.submission.feedback ?? '',
                        )
                        .catch(() => undefined);
                    };
                    return (
                      <tr
                        key={row.assignment.assignmentId}
                        className={`grading-student-row${selectedRow?.assignment.studentMembershipId === membershipId ? ' is-selected' : ''}`}
                        onClick={() => setSelectedStudentId(membershipId)}
                      >
                        <td>
                          <button
                            type="button"
                            className="grading-student-name"
                            aria-pressed={
                              selectedRow?.assignment.studentMembershipId === membershipId
                            }
                            aria-label={`选择学生 ${name}，${statusLabel(row.status)}`}
                            onClick={() => setSelectedStudentId(membershipId)}
                          >
                            <strong>{name}</strong>
                            {row.status === 'PENDING' ? (
                              <DotFilledIcon
                                className="grading-status--pending"
                                aria-label="待批改"
                                role="img"
                              />
                            ) : null}
                          </button>
                        </td>
                        <td className="grading-submitted-at">
                          {row.submission === null ? (
                            <span className="grading-muted">未提交</span>
                          ) : (
                            <time
                              dateTime={row.submission.submittedAt}
                              title={`${submittedAt}（北京时间）`}
                            >
                              <span>{submittedAt.split(' ')[0]}</span>{' '}
                              <span>{submittedAt.split(' ')[1]}</span>
                            </time>
                          )}
                        </td>
                        <td className="grading-elapsed">
                          {row.elapsedMs == null ? (
                            <span className="grading-muted">—</span>
                          ) : (
                            averageTimeLabel(row.elapsedMs).replaceAll(' ', '')
                          )}
                        </td>
                        <td>
                          <div
                            className="grading-verdicts"
                            role="group"
                            aria-label={`${name}的批改`}
                          >
                            <button
                              type="button"
                              className={row.status === 'CORRECT' ? 'is-correct' : ''}
                              aria-label={`${name}：判为正确`}
                              aria-pressed={row.status === 'CORRECT'}
                              disabled={!canReview || busy}
                              onClick={(event) => {
                                event.stopPropagation();
                                review('CORRECT');
                              }}
                            >
                              正确
                            </button>
                            <button
                              type="button"
                              className={row.status === 'INCORRECT' ? 'is-incorrect' : ''}
                              aria-label={`${name}：判为错误`}
                              aria-pressed={row.status === 'INCORRECT'}
                              disabled={!canReview || busy}
                              onClick={(event) => {
                                event.stopPropagation();
                                review('INCORRECT');
                              }}
                            >
                              错误
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length === 0 ? (
                <div className="empty-panel" role="status">
                  <strong>{busy ? '正在加载学生作答' : '暂无学生数据'}</strong>
                  <span>等待学生收到或提交作业。</span>
                </div>
              ) : null}
            </div>
          </section>
          <HomeworkGradingSummaryBar summary={summary} />
          <section className="homework-grading-console" aria-label="棋盘与批改操作">
            <div className="grading-toolbar">
              <span
                className="grading-current-student"
                title={`${selectedName} · ${selectedRow === undefined ? '' : statusLabel(selectedRow.status)}`}
              >
                <strong>{selectedName}</strong>
                {selectedRow === undefined ? null : (
                  <span className={`grading-status--${selectedRow.status.toLowerCase()}`}>
                    {' '}
                    · {statusLabel(selectedRow.status)}
                  </span>
                )}
              </span>
              <div className="homework-grading-tools" aria-label="批改工具">
                <button
                  type="button"
                  aria-label="查看学生作答"
                  aria-pressed={replayTarget?.mode === 'answer'}
                  disabled={selectedSubmissionId === null}
                  onClick={() => openSelectedReplay('answer')}
                >
                  <PersonIcon aria-hidden="true" />
                  学生作答
                </button>
                <button
                  type="button"
                  aria-label="查看落子轨迹"
                  aria-pressed={replayTarget?.mode === 'trace'}
                  disabled={selectedSubmissionId === null}
                  onClick={() => openSelectedReplay('trace')}
                >
                  <ListBulletIcon aria-hidden="true" />
                  落子轨迹
                </button>
                <button
                  type="button"
                  aria-pressed={researching}
                  disabled={replayTarget !== null || problemId === ''}
                  onClick={toggleResearch}
                >
                  <ResetIcon aria-hidden="true" />
                  {researching ? '结束研究' : '研究'}
                </button>
                <button
                  type="button"
                  aria-pressed={annotationOpen}
                  disabled={problemId === ''}
                  onClick={() => {
                    setReplayTarget(null);
                    setAnnotationOpen((open) => !open);
                  }}
                >
                  <Pencil2Icon aria-hidden="true" />
                  标记
                </button>
                <HomeworkMarkerControl
                  open={markerOptionsOpen}
                  onOpenChange={setMarkerOptionsOpen}
                  markerMode={markerMode}
                  customMarkerCount={customMarkerCount}
                  onMarkerMode={(mode, count) => {
                    setMarkerMode(mode);
                    if (count !== undefined) setCustomMarkerCount(count);
                  }}
                />
                <button
                  type="button"
                  className="homework-grading-zoom"
                  aria-label={zoomed ? '恢复全盘' : '棋盘放大'}
                  aria-pressed={zoomed}
                  disabled={focus === null}
                  onClick={() => setZoomed((value) => !value)}
                >
                  {zoomed ? <ZoomOutIcon aria-hidden="true" /> : <ZoomInIcon aria-hidden="true" />}
                  {zoomed ? '还原' : '放大'}
                </button>
              </div>
            </div>
            <div className="homework-grading-playback-dock" ref={setReplayControlsContainer}>
              {replayTarget !== null && selectedSubmissionId !== null ? null : (
                <HomeworkMoveControls
                  value={researching ? researchCursor : 0}
                  max={researching ? researchMoves.length : 0}
                  disabled={!researching}
                  busy={researchPending}
                  label={researching ? '研究变化' : '题目初始局面'}
                  onChange={setResearchCursor}
                />
              )}
            </div>
          </section>
          {homeworkState.error !== null ? (
            <HomeworkGradingErrorNotice
              message={homeworkState.error}
              canRetry={assignmentsLoadFailed}
              onRetry={retryAssignments}
              onDismiss={() => homework.clearMessages()}
            />
          ) : null}
        </aside>
      </div>
    </main>
  );
}
